import React, { useContext, useEffect, useRef, useState } from 'react';
import { UserContext } from 'contexts/ConfigContext';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { HookInputProps, outputType } from 'types/hooks';
import { TPODataContext } from 'contexts/TPOContext';
import { getAllPlansParser } from 'parser/tradePromoOptimization/plansParser';
import { parseCalenderData } from 'parser/tradePromoOptimization/eventsParser';
import { getConfig } from 'utils/helperMethods';
const errorMessage: string = 'Something went wrong!';

interface UpdatedOutputType extends outputType {
    // Non-deleted total. Same as `count` when the request was made with
    // exclude_deleted=true, otherwise = total_count - deleted_count. Used by
    // the Add Event modal to suggest an event-name suffix that's stable
    // regardless of whether the "Show Changes" toggle is on.
    countExcludingDelete: number;
}

function useFetchCalenderData({
    url,
    method,
    onComplete,
    onError,
    queryKey,
    variables = {},
    queryConfig = {},
    extras = {},
}: HookInputProps): UpdatedOutputType {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<any | null>(null);
    const [count, setCount] = useState(0);
    const [countExcludingDelete, setCountExcludingDelete] = useState(0);
    const { dimensionConfigData, kpiConfigData, promoLabelMapping, setMerchMDFPromoCount } = useContext(TPODataContext);
    const { appConfig } = useContext(UserContext);

    // Queries
    const {
        data: apiData,
        isFetching,
        isSuccess,
        fetchStatus,
        error: apiError,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            //only set data to null if function is being called outside/without infinite scroll
            //if offset is equal to default offset, it means request is coming without infinite scroll
            if (variables?.offset === 0) {
                setData(null);
                setError(null);
                setCount(0);
                setCountExcludingDelete(0);
                setMerchMDFPromoCount(0);
            }
            setLoading(true);

            try {
                const data = await axios({
                    method,
                    url,
                    data: { ...variables },
                });
                return data;
            } catch (error) {
                onFetchError(error);
            }
        },
        ...queryConfig,
        //  enabled: false,
    });

    const apiLoading = isFetching || fetchStatus === 'paused';
    const apiSuccess = !apiLoading && !apiError && isSuccess;
    //error handling
    if (!apiLoading && apiError && !error) {
        if (onError) return onError(apiError);
        onFetchError(apiError);
    }
    useEffect(() => {
        //success handling
        if (apiSuccess && apiData?.data) {
            if (error) setError(null);
            if (onComplete) return onComplete(apiData?.data);
            onFetchComplete(apiData.data);
        }
    }, [apiData?.data, apiSuccess]);

    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            const { calenderData, total_count, total_count_excluding_deleted, merch_mdf_promo_count } = payload;
            let mergedDataRes: any = [];
            const {
                result: parsedData,
                calenderColumns,
                nonCalenderColumns,
                hoverData,
                legendObj,
                statues,
            } = parseCalenderData(payload, kpiConfigData, promoLabelMapping, data?.legendObj ?? {}, appConfig);
            //if  offset is different from default offset value, it means request coming from infinite scroll
            if (variables?.offset !== 0 && Array.isArray(data?.result)) {
                //merge  old data, new data
                mergedDataRes = [...data?.result, ...parsedData];
            } else {
                mergedDataRes = parsedData;
            }
            setData({ result: mergedDataRes, calenderColumns, nonCalenderColumns, hoverData, legendObj, statues });
            setCount(total_count ?? 0);
            setCountExcludingDelete(total_count_excluding_deleted ?? 0);
            setMerchMDFPromoCount(merch_mdf_promo_count ?? 0);
        } catch (error) {
            console.error(errorMessage, error);
            onFetchError(error);
        } finally {
            setLoading(false);
        }
    }
    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        setData(null);
        setCount(0);
        setCountExcludingDelete(0);
        setLoading(false);
        setError(error);
    }

    const outputData: UpdatedOutputType = {
        loading,
        error,
        data,
        // total in the current request's mode (excludes deleted when the
        // request was made with exclude_deleted=true, includes them otherwise)
        count,
        countExcludingDelete,
    };
    return outputData;
}

export { useFetchCalenderData };
